import { BehaviorSubject, Observable, zip } from 'rxjs';
import { Address } from './address';
import { PropertyType } from './propertyType';
import { SaleType } from './saleType';
import { PropertyImage } from './propertyImage';
import { fitFromDTO, frameFromDTO } from '../shared/listing-image/listing-image.model';
import { PropertyAttachmentDTO } from '../DTO/propertyAttachmentDTO';
import { PropertyService } from '../services/PropertyService';
import { PropertySubType } from './propertySubType';
import { map, take } from 'rxjs/operators';
import { SoldPropertyDTO } from '../DTO/soldPropertyDTO';
import { Property } from './property';

export class SoldProperty{
    public id! : number;
    public propertyId! : string;
    public businessId! : string;
    public header? : string;
    public description? : string;
    public type? : PropertyType;
    public subType? : PropertySubType;
    public address? : Address;
    public saleType? : SaleType;
    public yearBuilt? : number;
    public lotSize? : number;
    public bedrooms? : number;
    public bathroomsFull? : number;
    public bathroomsHalf? : number;
    public garages? : number;
    public carPorts? : number;
    public area? : number;
    public stories? : string;
    public units? : number;
    public latitude? : number;
    public longitude? : number;
    public propertyImageArray : PropertyImage[];
    public soldAt? : Date;
    public soldFor? : number;
   
    public mappingFinishedSubject = new BehaviorSubject(false);

    public tag?:any; //used to attach a map marker

    constructor(){
        this.address=new Address();
        this.propertyImageArray = [];
    }

    public static mapToDTO(param:SoldProperty):SoldPropertyDTO{
        
        let dto = new SoldPropertyDTO();
            try
            {
                dto.id = param.id;
                dto.propertyId = param.propertyId;
                dto.businessId = param.businessId;
                dto.header=param.header;
                dto.description=param.description;
                dto.typeId=param.type?.id;
                dto.subTypeId=param.subType?.id;
                if(param.address) dto.address= Address.mapToDTO(param.address);
                dto.saleTypeId=param.saleType?.id;
                dto.yearBuilt=param.yearBuilt;
                dto.lotSize=param.lotSize;
                dto.bedrooms=param.bedrooms;
                dto.bathroomsFull=param.bathroomsFull;
                dto.bathroomsHalf=param.bathroomsHalf;
                dto.garages=param.garages;
                dto.carPorts=param.carPorts;
                dto.area=param.area;
                dto.stories=param.stories;
                dto.units=param.units;
                dto.latitude=param.latitude;
                dto.longitude=param.longitude;
                if(param.propertyImageArray){
                    param.propertyImageArray.forEach(i=>{
                        var a=new PropertyAttachmentDTO();
                        a.attachmentType=1;
                        a.id=i.id!;
                        a.reference=i.reference;
                        a.sequenceNo=i.sequenceNo;
                    });
                }
                if(param.soldAt) dto.soldAt=param.soldAt.toISOString();
                dto.soldFor=param.soldFor;
            }
            catch(e)
            {
                console.log(e);
            }
            return dto;
    }
    public static mapFromDTO(param:SoldPropertyDTO, propertyService:PropertyService):SoldProperty
    {
        
        let obj = new SoldProperty();
        let o1:Observable<void>;
        let o2:Observable<void>;
        try
        {
            obj.id = param.id;
            obj.propertyId = param.propertyId;
            obj.businessId = param.businessId;
            obj.header = param.header;
            obj.description = param.description;
            o1 = propertyService.getPropertyTypes().pipe(map(pc=>{
                obj.type=pc.find(t=>t.id==param.typeId);
                obj.subType=obj.type?.subTypeList.find(st=>st.id==param.subTypeId);
            }));

            if(param.address) obj.address = Address.mapFromDTO(param.address);
            o2 = propertyService.getSaleTypes().pipe(map(ls=>{
                obj.saleType=ls.find(s=>s.id==param.saleTypeId);
            }));
            obj.yearBuilt = param.yearBuilt;
            obj.lotSize=param.lotSize;
            obj.bedrooms=param.bedrooms;
            obj.bathroomsFull=param.bathroomsFull;
            obj.bathroomsHalf=param.bathroomsHalf;
            obj.garages=param.garages;
            obj.carPorts=param.carPorts;
            obj.area=param.area;
            obj.stories=param.stories;
            obj.units=param.units;
            obj.latitude=param.latitude;
            obj.longitude=param.longitude;
            zip(o1,o2).pipe(take(1),map(()=>{
                obj.mappingFinishedSubject.next(true);
            })).subscribe();
            if(param.attachments){
                param.attachments.forEach(a=>{
                    if(a.attachmentType==1){
                        let i=new PropertyImage();
                        i.id=a.id;
                        i.reference=a.reference;
                        i.sequenceNo=a.sequenceNo;
                        i.frame=frameFromDTO(a);
                        i.fit=fitFromDTO(a);
                        obj.propertyImageArray.push(i);
                    }
                });
            }
            if(param.soldAt) obj.soldAt = new Date(param.soldAt);
            obj.soldFor = param.soldFor;
        }
        catch(e)
        {
            console.log(e);
        }
        return obj;
    }
    public static mapFromProperty(param:Property):SoldProperty{
        const soldProperty = new SoldProperty();
        soldProperty.id = param.id;
        soldProperty.propertyId = param.propertyId;
        soldProperty.businessId = param.businessId;
        soldProperty.type = param.type;
        soldProperty.subType = param.subType;
        soldProperty.saleType = param.saleType;
        soldProperty.header = param.header;
        soldProperty.description = param.description;
        soldProperty.address = param.address;
        soldProperty.yearBuilt = param.yearBuilt;
        soldProperty.lotSize = param.lotSize;
        soldProperty.bedrooms = param.bedrooms;
        soldProperty.bathroomsFull = param.bathroomsFull;
        soldProperty.bathroomsHalf = param.bathroomsHalf;
        soldProperty.garages = param.garages;
        soldProperty.carPorts = param.carPorts;
        soldProperty.area = param.area;
        soldProperty.stories = param.stories;
        soldProperty.units = param.units;
        soldProperty.latitude = param.latitude;
        soldProperty.longitude = param.longitude;
        return soldProperty;
    }

    get detailsList(){
        let result:{id:number, label:string,value:string}[]=[];
        if(this.formattedAddress) result.push({id:0, label:"Address",value:this.formattedAddress});
        if(this.type) result.push({id:2, label:"Property Type",value:this.type.name+(this.subType?('('+this.subType.name+')'):'')});
        if(this.saleType) result.push({id:3, label:"Sale Type",value:this.saleType.displayName});
        if(this.address?.zipCode) result.push({id:5, label:"Zip Code", value:this.address.zipCode});
        if(this.yearBuilt) result.push({id:6, label:"Year Built", value:this.yearBuilt.toString()});
        if(this.area) result.push({id:7, label:"Square Footage",value:this.area.toLocaleString()+ ' sqft'});
        if(this.bedrooms) result.push({id:8, label:"Bedrooms",value:this.bedrooms.toString()});
        if(this.bathroomsFull || this.bathroomsHalf) result.push({id:10, label:"Bathrooms", value:(this.bathroomsFull?this.bathroomsFull.toString():'0')+'/'+(this.bathroomsHalf?this.bathroomsHalf.toString():'0')});
        if(this.type?.id==1 && this.stories) result.push({id:6, label:"Stories",value:this.stories.toLocaleString()});
        return result
    }

    get detailsListCard(){
        let result:{id:number, label:string,value:string}[]=[];
        switch(this.type?.id){
            case 1:
                //residential
                if(this.bedrooms) result.push({id:1, label:"bd",value:this.bedrooms.toString()});
                if(this.bathroomsFull || this.bathroomsHalf) result.push({id:2, label:"ba", value:(this.bathroomsFull?this.bathroomsFull.toString():'0')+(this.bathroomsHalf?('/'+this.bathroomsHalf.toString()):'')});
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                if(this.garages)result.push({id:4, label:"garage", value:this.garages.toString()});
                if(this.area) result.push({id:5, label:"sf",value:this.area.toLocaleString()});
                if(this.stories) result.push({id:6, label:"story",value:this.stories.toLocaleString()});
                break;
            case 2:
                if(this.subType?.id==210){
                    //Hospitality
                    if(this.units) result.push({id:0, label:"units",value:this.units.toString()});
                    if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                }
                else{
                    if(this.area) result.push({id:5, label:"sf",value:this.area.toLocaleString()});
                    if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                }
                break;  
            case 3:
                //multi-family
                if(this.units) result.push({id:0, label:"units",value:this.units.toString()});
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                break;
            case 4:
                //land
                if(this.lotSize) result.push({id:3, label:"acres",value:this.lotSize.toString()});
                break;
            default:
                break;
        }

        return result.slice(0,4);
    }
    
    get formattedAddress():string{
        if(!this.address) return '';
        return this.address.streetAddress1 + ', ' + ((this.address.streetAddress2?.length)?(this.address.streetAddress2 +', '):'') + this.address.city + ', ' + this.address.state + ' ' + this.address.zipCode;
    }

    get formattedAddress1():string{
        if(!this.address) return '';
        return this.address.streetAddress1 + ', ' + ((this.address.streetAddress2?.length)?(this.address.streetAddress2 +', '):'');
    }

    get formattedAddress2():string{
        if(!this.address) return '';
        return this.address.city + ', ' + this.address.state + ' ' + this.address.zipCode;
    }

    get searchData():string{
        if(!this.address) return '';
        return this.formattedAddress.toLowerCase() + ',' + this.address.county?.toLowerCase() + ',' + this.propertyId.toLowerCase();
    }

    get searchInfo():string{
        if(!this.address) return '';
        return this.formattedAddress + ',' + this.address.county + ', ID:' +this.id;
    }

    get center(){
         return (this.latitude && this.longitude)? { lat: this.latitude, lng: this.longitude}:undefined;
    }

    get totalBathRooms(){
        return (this.bathroomsFull?this.bathroomsFull:0)+(this.bathroomsHalf?this.bathroomsHalf:0);
    }

    get totalBathRoomsString(){
        return (this.bathroomsFull?this.bathroomsFull:0).toString()+' / '+(this.bathroomsHalf?this.bathroomsHalf:0).toString();
    }

    get propertyHeaderData1(){
        return this.saleType?.displayName + ' <span class=mx-3>|</span>' + this.type?.name;
    }

    get propertyHeaderData2(){
        switch(this.type?.id){
            case 1:
                //residential
                return (this.bedrooms?this.bedrooms:'0') + ' Bed <span class=mx-3>|</span>' + this.totalBathRooms + ' Bath <span class=mx-3>|</span>' + (this.area?this.area:'0') + ' SF <span class=mx-3>|</span>' +  (this.lotSize?this.lotSize:'0') +' Acres';
            case 2:
                //commercial
                return this.subType?.name + ' <span class=mx-3>|</span>' + (this.lotSize?this.lotSize:'0') +' Acres';
            case 3:
                //multi-family
                return (this.units?this.units:'0') + ' Units <span class=mx-3>|</span>' + (this.lotSize?this.lotSize:'0') +' Acres';
            case 4:
                //land
                return this.subType?.name + ' <span class=mx-3>|</span> ' + (this.lotSize?this.lotSize:'0') +' Acres';
                break;
            default:
                return '';
        }
    }

    get markerClassName(){
        return "home";
        /*
        switch(this.type.id){
            case 1:
                return "home";
            case 2:
                return "building";
            case 3:
                return "apartment";
            case 4:
                return "tree";
            default:
                return "default";
        }
        */
    }

    get firstImageReference(){
        return this.propertyImageArray?.length>0?this.propertyImageArray[0].reference:undefined;
    }

    get dashboardMarkerColor(){
        return 'transparent';
    }

    //return center in exchange for a tag (object like a map marker or so)
    public getCenter(tag:any){
        this.tag=tag;
        return this.center;
    }
}
